import * as React from "react";
import { isEmpty, trim, last } from "lodash";

import {
  findNodesLineages,
} from "../../../helpers/Doc/Functions/base";

import { DIAGNOSES, FINDINGS, NOTES } from "../../../reducers/ui/reasoningTool";
import ReasoningToolDiagnosisSummary from "./DiagnosisSummary";
import ReasoningToolNoteSummary from "./NoteSummary";
import ReasoningToolFindingSummary from "./FindingSummary";
import ReasoningToolPanel from "./Panel";
const titles = {
  [DIAGNOSES]: "Diagnoses",
  [FINDINGS]: "Findings",
  [NOTES]: "Notes",
};

const infoBoxes = {
  [NOTES]: "Click to add a new note below.  These notes are only visible to you.",
};

const panelComponents = {
  [DIAGNOSES]: ReasoningToolDiagnosisSummary,
  [FINDINGS]: ReasoningToolFindingSummary,
  [NOTES]: ReasoningToolNoteSummary,
};

function attachNodesToNotes(notes: Array<DocNote>, doc) {
  const noteNodesUids = notes.map((note) => note.notedNodeUid).filter((uid) => uid);
  const noteNodeLineages = findNodesLineages(doc, (node) => node.type && noteNodesUids.includes(node.uid));
  const noteNodeLineagesLookup = Object.fromEntries(noteNodeLineages.map((noteNodeLineage: Array<any>) => [last(noteNodeLineage).uid, noteNodeLineage])); // TODO: Remove Array<any>

  return notes.map((note) => { return {...note, noteNodeLineage: noteNodeLineagesLookup[note.notedNodeUid]}; });
}

interface ReasoningToolTextListPanelProps {
  wide: boolean;
  documentContext: DocumentContext;
  displayContext: DisplayContext;
  userContext: UserContext;
  reasoningToolContext: ReasoningToolContext;
  leadingDiagnosis: string;
  diagnoses: Array<DocDiagnosis>;
  findings: Array<DocFinding>;
  notes: Array<DocNote>;
};


export default function ReasoningToolTextListPanel(props: ReasoningToolTextListPanelProps) {
  const {
    wide,
    documentContext,
    displayContext,
    userContext,
    reasoningToolContext,
    leadingDiagnosis,
    diagnoses = [],
    findings = [],
    notes = [],
  } = props;

  // For Diagnoses panel: refs for each diagnosis row, their action icons, and the Add button
  const diagnosisRowRefs = React.useRef([]);
  const diagnosisActionRefs = React.useRef([]); // Array of [dangerousRef, leadingRef] for each row
  const addButtonRef = React.useRef(null);

  const {
    activePanel,
    togglePanelDeleting,
    panelNodeEditStart,
    panelNodeEditEnd,
    isPanelDeleting,
    activeEditNodeUid,
  } = reasoningToolContext;


  let items = [];
  let panelData : {
    addNode: (uid: string, data?: Record<string, any>) => void;
    updateNode: (uid: string, data: Record<string, any>) => void;
    deleteNode: (uid: string) => void;
    setIsDangerousDiagnosis?: (uid: string, value: boolean) => void;
    setIsLeadingDiagnosis?: (uid: string) => void;
    userContext?: UserContext;
  }
  switch (activePanel) {
    case DIAGNOSES:
      panelData = {
        addNode: userContext.addDiagnosis,
        updateNode: userContext.updateDiagnosis,
        deleteNode: userContext.deleteDiagnosis,
        setIsDangerousDiagnosis: userContext.setIsDangerousDiagnosis,
        setIsLeadingDiagnosis: userContext.setIsLeadingDiagnosis,
      };

      items = diagnoses;
      break;
    case FINDINGS:
      panelData = {
        addNode: userContext.addFinding,
        updateNode: userContext.updateFinding,
        deleteNode: userContext.deleteFinding,
      };

      items = findings;
      break;
    case NOTES:
      panelData = {
        addNode: userContext.addNote,
        updateNode: userContext.updateNote,
        deleteNode: userContext.deleteNote,
        userContext: userContext,
      };

      items = attachNodesToNotes(notes, props);
      break;
    default:
      return null;
  }

  const PanelComponent = panelComponents[activePanel];

  // For Diagnoses: custom render to inject refs and keyboard handlers
  let renderedItems;
  if (activePanel === DIAGNOSES) {
    renderedItems = items.map((item, idx) => {
      const startNodeEdit = () => panelNodeEditStart(item.uid);

      const endNodeEdit = (e: React.FocusEvent<HTMLInputElement>) => {
        if (isEmpty(trim(e.target.value))) {
          panelData.deleteNode(item.uid);
          displayContext.setFocusedBookmarkUid(null);
        } else {
          panelData.updateNode(item.uid, {content: e.target.value}),
          panelNodeEditEnd(item.uid);
          setTimeout(() => {
            const panel = document.querySelector('.reasoning-tool-panel');
            if (panel) {
              const addButton = panel.querySelector('.reasoning-tool-add-button') as HTMLElement;
              const firstFocusable = panel.querySelector('button, [role="button"][tabindex="0"], a[href], textarea:not([disabled]), input:not([disabled]):not([type="hidden"])') as HTMLElement;
              const targetElement = addButton || firstFocusable;
              if (targetElement) {
                targetElement.focus();
              }
            }
          }, 50);
        }
      };

      // Refs for row and action icons
      if (!diagnosisRowRefs.current[idx]) diagnosisRowRefs.current[idx] = React.createRef();
      if (!diagnosisActionRefs.current[idx]) diagnosisActionRefs.current[idx] = [React.createRef(), React.createRef()];

      // Keyboard navigation for row
      const handleRowKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          if (diagnosisRowRefs.current[idx + 1]) {
            diagnosisRowRefs.current[idx + 1].current && diagnosisRowRefs.current[idx + 1].current.focus();
          } else {
            // Last row: go to Add button
            addButtonRef.current && addButtonRef.current.focus();
          }
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          if (idx > 0 && diagnosisRowRefs.current[idx - 1]) {
            diagnosisRowRefs.current[idx - 1].current && diagnosisRowRefs.current[idx - 1].current.focus();
          } else {
            // First row: go to edit icon
            const editIcon = document.querySelector('.reasoning-tool-panel-header-delete-button');
            if (editIcon) (editIcon as HTMLElement).focus();
          }
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          diagnosisActionRefs.current[idx][0].current && diagnosisActionRefs.current[idx][0].current.focus();
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          if (idx > 0) {
            // Previous row's star icon
            diagnosisActionRefs.current[idx - 1][1].current && diagnosisActionRefs.current[idx - 1][1].current.focus();
          } else {
            // First row: go to Add button
            addButtonRef.current && addButtonRef.current.focus();
          }
        }
      };

      // Keyboard navigation for action icons handled in DiagnosisSummary

      return (
        <div
          key={item.uid}
          ref={diagnosisRowRefs.current[idx]}
          tabIndex={0}
          className="diagnosis-row-accessible"
          onKeyDown={handleRowKeyDown}
        >
          <PanelComponent
            {...item}
            isEditing={!!activeEditNodeUid && item.uid === activeEditNodeUid}
            startNodeEdit={startNodeEdit}
            endNodeEdit={endNodeEdit}
            isLeadingDiagnosis={!!leadingDiagnosis && item.uid === leadingDiagnosis}
            documentContext={documentContext}
            displayContext={displayContext}
            {...panelData}
            deleteNode={panelData.deleteNode}
            // Pass refs for icons
            dangerousRef={diagnosisActionRefs.current[idx][0]}
            leadingRef={diagnosisActionRefs.current[idx][1]}
            rowIdx={idx}
            totalRows={items.length}
            diagnosisRowRefs={diagnosisRowRefs}
          />
        </div>
      );
    });
  } else {
    renderedItems = items.map((item) => {
      const startNodeEdit = () => panelNodeEditStart(item.uid);

      const endNodeEdit = (e: React.FocusEvent<HTMLInputElement>) => {
        if (isEmpty(trim(e.target.value))) {
          panelData.deleteNode(item.uid);
          displayContext.setFocusedBookmarkUid(null);
        } else {
          panelData.updateNode(item.uid, {content: e.target.value}),
          panelNodeEditEnd(item.uid);
          setTimeout(() => {
            const panel = document.querySelector('.reasoning-tool-panel');
            if (panel) {
              const addButton = panel.querySelector('.reasoning-tool-add-button') as HTMLElement;
              const firstFocusable = panel.querySelector('button, [role="button"][tabindex="0"], a[href], textarea:not([disabled]), input:not([disabled]):not([type="hidden"])') as HTMLElement;
              const targetElement = addButton || firstFocusable;
              if (targetElement) {
                targetElement.focus();
              }
            }
          }, 50);
        }
      };

      return (
        <PanelComponent
          {...item}
          key={item.uid}
          isEditing={!!activeEditNodeUid && item.uid === activeEditNodeUid}
          startNodeEdit={startNodeEdit}
          endNodeEdit={endNodeEdit}
          isLeadingDiagnosis={!!leadingDiagnosis && item.uid === leadingDiagnosis}
          documentContext={documentContext}
          displayContext={displayContext}
          {...panelData}
        />
      );
    });
  }

  // Focus management: focus first actionable element when Diagnoses panel opens (not on every diagnoses change)
  React.useEffect(() => {
    if (activePanel === DIAGNOSES && diagnosisRowRefs.current.length > 0) {
      diagnosisRowRefs.current[0]?.current?.focus();
    }
    // Only run when panel is opened
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePanel]);

  const availableOffline = activePanel === FINDINGS || activePanel === DIAGNOSES;

  return (
    <ReasoningToolPanel
      title={titles[activePanel]}
      infoBox={infoBoxes[activePanel]}
      isDeleting={isPanelDeleting}
      toggleDeleting={togglePanelDeleting}
      isAddable
      availableOffline={availableOffline}
      wide={wide}
      panelNodeEditStart={panelNodeEditStart}
      displayContext={displayContext}
      activePanelType={activePanel}
      {...panelData}
      addButtonRef={addButtonRef}
    >
      {renderedItems}
    </ReasoningToolPanel>
  );
}
